import { Err, Ok, Result } from 'ts-res';
import { ExternalPackageInfoType } from 'src/sdkInfoTypes';
import { KameleoonError, KameleoonException } from 'src/kameleoonError';
import { ConfigurationDataType, JSONType } from 'src/clientConfiguration';
import { Header, NUMBER_OF_RETRIES } from './constants';
import {
  RequesterParamsType,
  GetVisitorDataResultType,
  HeadersType,
  GetVisitorDataParamsType,
  IExternalRequester,
  RequestType,
  HttpMethod,
  InternalSendRequestParametersType,
  KameleoonResponseType,
  RequestParametersType,
  logRequestParametersType,
  logResponse,
} from './types';
import { IUrlProvider } from './urlProvider';
import { KameleoonLogger, LogLevel } from 'src/logging';
import { ResultConfigurationDataType } from 'src/clientConfiguration/types';

export interface IRequester {
  getClientConfiguration: () => Promise<
    Result<ResultConfigurationDataType, KameleoonError>
  >;
  getRemoteData: (key: string) => Promise<Result<JSONType, KameleoonError>>;
  track: (
    body: string,
    isBodyUserAgent: boolean,
  ) => Promise<Result<void, void>>;
}

export class Requester implements IRequester {
  private externalRequester: IExternalRequester;
  private urlProvider: IUrlProvider;
  private packageInfo: ExternalPackageInfoType;
  private trackRetryDelay: number;
  private useAbortController: boolean;
  private timeout: number;

  constructor({
    urlProvider,
    packageInfo,
    externalRequester,
    requestTimeout,
    trackRetryDelay,
    useAbortController,
  }: RequesterParamsType) {
    KameleoonLogger.debug`CALL: new Requester(urlProvider, packageInfo: ${packageInfo}, externalRequester, requestTimeout: ${requestTimeout}, trackRetryDelay: ${trackRetryDelay}, useAbortController: ${useAbortController})`;
    this.urlProvider = urlProvider;
    this.useAbortController = useAbortController;
    this.externalRequester = externalRequester;
    this.trackRetryDelay = trackRetryDelay;
    this.packageInfo = packageInfo;
    this.timeout = requestTimeout;
    KameleoonLogger.debug`RETURN: new Requester(urlProvider, packageInfo: ${packageInfo}, externalRequester, requestTimeout: ${requestTimeout}, trackRetryDelay: ${trackRetryDelay}, useAbortController: ${useAbortController})`;
  }

  public async getClientConfiguration(
    timeStamp?: number,
    modifiedSince?: string,
  ): Promise<Result<ResultConfigurationDataType, KameleoonError>> {
    const requestUrl = this.urlProvider.getClientConfigurationUrl(timeStamp);

    let headers: HeadersType = {
      [Header.SdkType]: this.packageInfo.type.toLowerCase(),
      [Header.SdkVersion]: this.packageInfo.version,
      ...(modifiedSince && { [Header.IfModifiedSince]: modifiedSince }),
    };

    // --- Note ---
    // Initial request + NUMBER_OF_RETRIES
    const requests = NUMBER_OF_RETRIES + 1;

    try {
      let responseInfo = { message: '' };

      KameleoonLogger.debug`Running configuration request Method: ${HttpMethod.Get}, Url: ${requestUrl}, Headers: ${headers}, with retry limit ${requests}`;
      for (let i = 0; i < requests; i++) {
        const response = await this.sendRequest<RequestType.Configuration>({
          url: requestUrl,
          requestType: RequestType.Configuration,
          retryCount: i,
          parameters: {
            method: HttpMethod.Get,
            headers,
          },
        });

        if (response.ok) {
          const data = await response.json();

          this.logReceivedConfigurationResponse(response.status, requestUrl, headers);

          const lastModified = this.getLastModifiedHeader(response);
          const configurationData = data as ConfigurationDataType;

          return Ok({
            configuration: configurationData,
            lastModified: lastModified,
          });
        }

        if (response.status === 304) {
          this.logReceivedConfigurationResponse(response.status, requestUrl, headers);

          return Ok({});
        }

        if (i === NUMBER_OF_RETRIES) {
          if (response.text) {
            const text = await response.text();
            responseInfo = JSON.parse(text);
          }
          KameleoonLogger.error`Failed to get configuration response ${{
            status: response.status,
            message: responseInfo.message,
          }} for request Method: ${
            HttpMethod.Get
          }, Url: ${requestUrl}, Headers: ${headers}`;
        } else {
          await this.logRequestError({
            logLevel: LogLevel.WARNING,
            message: 'Failed to get configuration response',
            response: response,
            method: HttpMethod.Get,
            url: requestUrl,
          });
        }
      }

      return Err(
        new KameleoonError(
          KameleoonException.MaximumRetriesReached,
          responseInfo.message,
        ),
      );
    } catch (err) {
      await this.logRequestError({
        logLevel: LogLevel.ERROR,
        message: 'Failed to get configuration response',
        error: err,
        method: HttpMethod.Get,
        url: requestUrl,
        headers,
      });
      return Err(
        new KameleoonError(KameleoonException.ClientConfiguration, err),
      );
    }
  }

  public async getRemoteData(
    key: string,
  ): Promise<Result<JSONType, KameleoonError>> {
    const requestUrl = this.urlProvider.getRemoteDataUrl(key);

    KameleoonLogger.debug`Running remote data request Method: ${HttpMethod.Get}, Url: ${requestUrl}`;
    try {
      const response = await this.sendRequest<RequestType.RemoteData>({
        url: requestUrl,
        requestType: RequestType.RemoteData,
        retryCount: 0,
        parameters: {
          method: HttpMethod.Get,
        },
      });

      if (!response.ok) {
        await this.logRequestError({
          logLevel: LogLevel.ERROR,
          message: 'Failed to get remote data response',
          response: response,
          method: HttpMethod.Get,
          url: requestUrl,
        });
        if (response.text) {
          const text = await response.text();
          const message = JSON.parse(text)?.message;

          return Err(
            new KameleoonError(KameleoonException.RemoteData, message),
          );
        }

        return Err(
          new KameleoonError(
            KameleoonException.RemoteData,
            'Unknown Reason - no `text()` was found for a response',
          ),
        );
      }

      const data = await response.json();

      KameleoonLogger.debug`Received remote data response: ${{
        status: response.status,
      }} for request Method: ${HttpMethod.Get}, Url: ${requestUrl}`;
      return Ok(data);
    } catch (err) {
      await this.logRequestError({
        logLevel: LogLevel.ERROR,
        message: 'Failed to get remote data response',
        error: err,
        method: HttpMethod.Get,
        url: requestUrl,
      });
      return Err(new KameleoonError(KameleoonException.RemoteData, err));
    }
  }

  public async getVisitorData({
    visitorCode,
    filters,
    isMappingIdentifier,
  }: GetVisitorDataParamsType): Promise<
    Result<GetVisitorDataResultType, KameleoonError>
  > {
    const requestUrl = this.urlProvider.getVisitorDataUrl({
      visitorCode,
      filters,
      isMappingIdentifier,
    });

    KameleoonLogger.debug`Running visitor data request Method: ${HttpMethod.Get}, Url: ${requestUrl}`;
    try {
      const response = await this.sendRequest<RequestType.RemoteData>({
        url: requestUrl,
        requestType: RequestType.RemoteData,
        retryCount: 0,
        parameters: {
          method: HttpMethod.Get,
        },
      });

      if (!response.ok) {
        await this.logRequestError({
          logLevel: LogLevel.ERROR,
          message: 'Failed to get visitor data response',
          response: response,
          method: HttpMethod.Get,
          url: requestUrl,
        });
        if (typeof response.text === 'function') {
          const text = await response.text();

          if (text) {
            const message = JSON.parse(text)?.message;

            return Err(
              new KameleoonError(KameleoonException.RemoteData, message),
            );
          }
        }

        if (response.status) {
          return Err(
            new KameleoonError(
              KameleoonException.RemoteData,
              `No error message. Error status: ${response.status}`,
            ),
          );
        }

        return Err(
          new KameleoonError(
            KameleoonException.RemoteData,
            'Unknown Reason - no text message or error status was found for a response',
          ),
        );
      }

      const data = await response.json();

      KameleoonLogger.debug`Received visitor data response: ${{
        status: response.status,
      }} for request Method: ${HttpMethod.Get}, Url: ${requestUrl}`;
      return Ok(data as GetVisitorDataResultType);
    } catch (err) {
      await this.logRequestError({
        logLevel: LogLevel.ERROR,
        message: 'Failed to get visitor data response',
        error: err,
        method: HttpMethod.Get,
        url: requestUrl,
      });
      return Err(new KameleoonError(KameleoonException.RemoteData, err));
    }
  }

  public async track(
    body: string,
    isBodyUserAgent: boolean,
  ): Promise<Result<void, void>> {
    // --- Note ---
    // Initial request + NUMBER_OF_RETRIES
    const requests = NUMBER_OF_RETRIES + 1;
    const url = this.urlProvider.getTrackingUrl(isBodyUserAgent);

    KameleoonLogger.debug`Running tracking request Method: ${HttpMethod.Post}, Url: ${url}, Body: ${body}, with retry limit ${requests}, retry delay ${this.trackRetryDelay} ms`;
    for (let i = 0; i < requests; i++) {
      let response;
      try {
        response = await this.sendRequest<RequestType.Tracking>({
          url,
          retryCount: i,
          requestType: RequestType.Tracking,
          parameters: {
            body,
            method: HttpMethod.Post,
            headers: {
              [Header.ContentType]: '*/*',
            },
          },
        });

        if (response.ok) {
          KameleoonLogger.debug`Received tracking response: ${{
            status: response.status,
          }} for request Method: ${
            HttpMethod.Post
          }, Url: ${url}, Body: ${body}`;
          return Ok();
        }

        await this.logRequestError({
          logLevel: i == NUMBER_OF_RETRIES ? LogLevel.ERROR : LogLevel.WARNING,
          message: 'Failed to get tracking response',
          response: response,
          method: HttpMethod.Post,
          url,
        });
      } catch (error) {
        await this.logRequestError({
          logLevel: i == NUMBER_OF_RETRIES ? LogLevel.ERROR : LogLevel.WARNING,
          message: 'Failed to get tracking response',
          error,
          method: HttpMethod.Post,
          url,
        });
      }

      if (i < requests) {
        await new Promise((resolve) =>
          setTimeout(resolve, this.trackRetryDelay),
        );
      }
    }

    return Err();
  }

  private async sendRequest<T extends RequestType>({
    url,
    requestType,
    retryCount,
    parameters,
  }: InternalSendRequestParametersType<T>): Promise<KameleoonResponseType> {
    let response;

    if (this.useAbortController) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      response = await this.externalRequester.sendRequest({
        url,
        retryCount: retryCount as 0 | 1 | 2,
        requestType,
        parameters: {
          ...parameters,
          signal: controller.signal,
        } as RequestParametersType[T],
      });

      clearTimeout(timeoutId);
    } else {
      response = await this.externalRequester.sendRequest({
        url,
        retryCount: retryCount as 0 | 1 | 2,
        requestType,
        parameters: parameters as RequestParametersType[T],
      });
    }

    return response;
  }

  private async logRequestError({
    logLevel,
    message,
    error,
    method,
    url,
    response,
    headers,
  }: logRequestParametersType): Promise<void> {
    if (error) {
      let errorText = 'Unknown error';
      if (error instanceof Error) {
        errorText = error.message;
      }
      KameleoonLogger.log({
        level: logLevel,
        strings: () =>
          `${message} with error: ${errorText} for request Method: ${method}, Url: ${url}`,
      });
    } else if (response) {
      if (KameleoonLogger.checkLevel(logLevel)) {
        const logResponse = await this.parseResponse(response);
        KameleoonLogger.log({
          level: logLevel,
          strings: () =>
            `${message} response: Status: '${logResponse.status}' Message: '${logResponse.message}' for request Method: ${method}, Url: '${url}'` +
            (headers ? `, Headers: ${headers}` : ''),
        });
      }
    }
  }

  private async parseResponse(
    response: KameleoonResponseType,
  ): Promise<logResponse> {
    let message = '';
    try {
      if (typeof response.text === 'function') {
        const text = await response.text();
        if (text) {
          message = text;
        }
      }
    } catch (error) {}

    return {
      status: response.status,
      message: message,
    };
  }

  private getLastModifiedHeader(
    response: KameleoonResponseType,
  ): string | undefined {
    if (response.headers) {
      const targetKey = Header.LastModified.toLowerCase();
      for (const [key, value] of response.headers) {
        if (key.toLowerCase() === targetKey) {
          return value;
        }
      }
    }
    return undefined;
  }

  private logReceivedConfigurationResponse(responseStatus: number, requestUrl: string, headers: HeadersType): void {
    KameleoonLogger.debug`Received configuration response: ${{
      status: responseStatus,
    }} for request Method: ${
      HttpMethod.Get
    }, Url: ${requestUrl}, Headers: ${headers}`;
  }
}
